// 頁首使用者選單〔<顯示名> ▾〕（SPEC #vms-web-220 頂列，VMS UX 評審 2026-10-13 P1-10）：
// 我的帳號（/settings）、開發者帳號（我的帳號的一個分頁）、語言、時間顯示（場域時區 / 我的時區）、地圖供應商、登出。
// 原本頂列常駐的〔開發者帳號〕按鈕與語言下拉收進這裡。
// 最下方一行版本資訊＋〔複製版本資訊〕（P2-1）：「版本 <部署的 artifact id>（網頁 <commit>）」——/config.json 的 version 是
// artifact id、build 是 commit（第二輪 R2-P1-10、B2-17）；伺服器沒給 build 就用網頁建置時的 commit。另附目前網址、瀏覽器。
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LANGS, setLanguage } from '../../i18n';
import { useApp } from '../../lib/AppContext';
import { logout } from '../../lib/session';
import { useTzPref } from '../../lib/useTz';
import { useScope } from '../../lib/scope';
import { reporterOrgs } from '../../lib/supportApi';
import { vmsApi } from '../../lib/planeApi';
import { usePrefs } from '../map/useMapData';

/** 版本那一行的「網頁 <commit>」：伺服器給的 build（部署時的 commit）優先，否則網頁建置時的 commit。 */
export function webBuildLabel(serverBuild: string | undefined, webBuild: string | undefined): string {
  return serverBuild || (webBuild && webBuild !== 'dev' ? webBuild : '') || 'dev';
}

export function UserMenu() {
  const { t, i18n } = useTranslation();
  const { session, config } = useApp();
  const [copied, setCopied] = useState(false);
  const build = webBuildLabel(config.build, typeof __WEB_BUILD__ === 'string' ? __WEB_BUILD__ : undefined);
  const versionLine = t('user.version', { brand: config.display_name, version: config.version || 'dev', build });
  const copyVersion = async () => {
    const text = [versionLine, location.href, navigator.userAgent].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* 瀏覽器不給剪貼簿：版本那行可以自己選取 */
    }
  };
  const { mode, setMode } = useTzPref();
  // 地圖供應商（SPEC #vms-web-220 使用者選單；使用者覆寫組織預設，存 /v1/me/vms/prefs 的 map_provider；空 = 依地區）
  const { planes, orgs } = useScope();
  const canReport = reporterOrgs(orgs).length > 0; // 問題回報（SPEC #support-business）：有回報權才出現
  const api = planes[0]?.api;
  const prefs = usePrefs(api);
  const qc = useQueryClient();
  const mapProvider = prefs.data?.map_provider || '';
  const setMapProvider = async (p: '' | 'google' | 'baidu') => {
    if (!api) return;
    await vmsApi.setPrefs(api, { ...(prefs.data ?? {}), map_provider: p }).catch(() => {});
    await qc.invalidateQueries({ queryKey: ['vms-prefs'] });
  };
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const name = session?.user?.display_name || session?.user?.email || t('user.me');
  useEffect(() => {
    if (!open) return;
    const off = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', off);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', off);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  const close = () => setOpen(false);
  return (
    <div className="user-menu" ref={ref}>
      <button className="btn user-menu-btn" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)} data-testid="user-menu">
        <span className="user-initial" aria-hidden>
          {(name || '?').trim().slice(0, 1).toUpperCase()}
        </span>
        <span className="user-name">{name}</span> <span aria-hidden>▾</span>
      </button>
      {open && (
        <div className="user-menu-panel card" role="menu" aria-label={t('user.menu')}>
          {session?.user?.email && session.user.display_name && <div className="muted small user-menu-email">{session.user.email}</div>}
          <Link role="menuitem" to="/settings" onClick={close} data-testid="user-my-account">
            {t('nav.account')}
          </Link>
          {canReport && (
            <Link role="menuitem" to="/support" onClick={close} data-testid="user-support">
              {t('support.title')}
            </Link>
          )}
          <Link role="menuitem" to="/settings/developer-account" onClick={close} data-testid="user-developer-account">
            {t('devacct.tab')}
          </Link>
          <label className="user-menu-field">
            <span>{t('shell.language')}</span>
            <select className="input" aria-label={t('shell.language')} value={i18n.language} onChange={(e) => setLanguage(e.target.value)}>
              {LANGS.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.label}
                </option>
              ))}
            </select>
          </label>
          <label className="user-menu-field">
            <span>{t('user.tz')}</span>
            <select className="input" aria-label={t('user.tz')} value={mode} onChange={(e) => void setMode(e.target.value as 'site' | 'browser')}>
              <option value="site">{t('user.tz_site')}</option>
              <option value="browser">{t('user.tz_browser')}</option>
            </select>
          </label>
          <label className="user-menu-field">
            <span>{t('user.map_provider')}</span>
            <select
              className="input"
              aria-label={t('user.map_provider')}
              value={mapProvider}
              disabled={!api}
              onChange={(e) => void setMapProvider(e.target.value as '' | 'google' | 'baidu')}
              data-testid="user-map-provider"
            >
              <option value="">{t('user.map_auto')}</option>
              <option value="google">{t('map.provider_google')}</option>
              <option value="baidu">{t('map.provider_baidu')}</option>
            </select>
          </label>
          <button role="menuitem" className="btn" onClick={() => void logout()}>
            {t('shell.logout')}
          </button>
          <div className="user-menu-version muted small" data-testid="user-version">
            <span>{versionLine}</span>
            <button type="button" className="link-btn" onClick={() => void copyVersion()}>
              {copied ? t('user.version_copied') : t('user.version_copy')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
